import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Package } from "lucide-react";
import { PageHeader, PageContent, EmptyState, Tabs } from "@printtat/ui";
import { orderApi } from "@/services/orderApi";
import { queryKeys } from "@/lib/queryKeys";
import { OrderCard } from "./components/OrderCard";
import type { OrderListItem, OrderStatus } from "@printtat/shared";

type StatusFilter = "all" | "active" | "completed" | "cancelled";

const FILTER_TABS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const ACTIVE_STATUSES: OrderStatus[] = [
  "pending",
  "confirmed",
  "in_production",
  "quality_check",
  "shipped",
  "out_for_delivery",
];

const filterOrders = (
  orders: OrderListItem[],
  filter: StatusFilter
): OrderListItem[] => {
  switch (filter) {
    case "active":
      return orders.filter((o) =>
        ACTIVE_STATUSES.includes(o.status as OrderStatus)
      );
    case "completed":
      return orders.filter((o) => o.status === "delivered");
    case "cancelled":
      return orders.filter(
        (o) => o.status === "cancelled" || o.status === "returned"
      );
    default:
      return orders;
  }
};

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-md ${className}`} />
);

const OrdersSkeleton = () => (
  <PageContent>
    {/* Header */}
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Skeleton className="w-5 h-5" />
        <Skeleton className="w-24 h-6" />
      </div>
      <Skeleton className="w-48 h-4 mt-1" />
    </div>

    {/* Tabs */}
    <div className="flex gap-2 mb-6">
      {["All", "Active", "Completed", "Cancelled"].map((t) => (
        <Skeleton key={t} className="w-20 h-8 rounded-full" />
      ))}
    </div>

    {/* Order cards */}
    <div className="space-y-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
        >
          <Skeleton className="w-14 h-14 rounded-lg" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Skeleton className="w-40 h-4" />
              <Skeleton className="w-16 h-5 rounded-full" />
            </div>
            <Skeleton className="w-32 h-3" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-4 h-4" />
          </div>
        </div>
      ))}
    </div>
  </PageContent>
);

export const OrdersPage = () => {
  const [filter, setFilter] = useState<StatusFilter>("all");

  const { data: orders = [], isLoading, error } = useQuery({
    queryKey: queryKeys.orders.all(),
    queryFn: () => orderApi.getOrders(),
  });

  if (isLoading) {
    return <OrdersSkeleton />;
  }

  const filtered = filterOrders(orders, filter);

  return (
    <PageContent>
      <PageHeader
        icon={Package}
        title="Orders"
        description="Track and manage your orders"
      />

      <div className="mt-6">
        <Tabs tabs={FILTER_TABS} value={filter} onChange={setFilter} variant="pill" />
      </div>

      <div className="mt-6">
        {error ? (
          <div className="text-center py-12 text-gray-500">
            {error instanceof Error ? error.message : "Failed to load orders"}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Package}
            title={
              filter === "all"
                ? "No orders yet"
                : `No ${filter} orders`
            }
            description={
              filter === "all"
                ? "Once you place an order, it will appear here"
                : "Try a different filter to find your orders"
            }
          />
        ) : (
          <div className="space-y-3">
            {filtered.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        )}
      </div>
    </PageContent>
  );
};
